/**
 * Codes de rôles valides pour les membres de classe
 */
export const CLASS_ROLE_CODES = {
  STUDENT: 'STUDENT',
  TEACHER: 'TEACHER',
  MAIN_TEACHER: 'MAIN_TEACHER',
  DELEGATE: 'DELEGATE',
} as const

export type ClassRoleCode =
  (typeof CLASS_ROLE_CODES)[keyof typeof CLASS_ROLE_CODES]

/**
 * Code de rôle par défaut pour les nouveaux membres
 */
export const DEFAULT_CLASS_ROLE_CODE = CLASS_ROLE_CODES.STUDENT

/**
 * Rôles établissement autorisés pour rattachement à une classe
 * Note: Les constantes détaillées sont dans establishment-membership.constants.ts
 */
export const ALLOWED_CLASS_MEMBER_ROLES = ['STUDENT', 'TEACHER'] as const

export type AllowedClassMemberRole = (typeof ALLOWED_CLASS_MEMBER_ROLES)[number]

/**
 * Messages d'erreur
 */
export const CLASS_MEMBERSHIP_ERRORS = {
  NOT_FOUND: 'Adhésion classe non trouvée',
  CLASS_NOT_FOUND: 'Classe non trouvée',
  ALREADY_DELETED: 'Adhésion classe déjà supprimée',
  ALREADY_MEMBER: 'Utilisateur déjà membre de la classe',
  ACCESS_DENIED: 'Accès interdit à cette classe',
  INSUFFICIENT_PERMISSIONS: 'Permissions insuffisantes',
  CREATION_FAILED: 'Erreur lors de la création',
  UPDATE_FAILED: 'Erreur lors de la mise à jour',
  DELETE_FAILED: 'Erreur lors de la suppression',
  INVALID_ROLE_CODE: (code: string) =>
    `Rôle de classe "${code}" non trouvé. Codes valides: ${Object.values(CLASS_ROLE_CODES).join(', ')}`,
  USER_NOT_IN_ESTABLISHMENT: (userId: string) =>
    `L'utilisateur ${userId} n'est pas membre de l'établissement de cette classe`,
  USER_INVALID_ESTABLISHMENT_ROLE: (userId: string, role: string) =>
    `L'utilisateur ${userId} a le rôle "${role}" dans l'établissement. Seuls les TEACHER et STUDENT peuvent être rattachés à une classe.`,
  MAIN_TEACHER_ALREADY_EXISTS:
    "Cette classe a déjà un professeur principal. Il ne peut y en avoir qu'un seul.",
  LESSON_NOT_FOUND: 'Leçon non trouvée',
} as const

/**
 * Opérations pour les metrics
 */
export const CLASS_MEMBERSHIP_OPERATIONS = {
  CREATE: 'create_class_membership',
  FIND_ALL: 'findMany_class_memberships',
  FIND_ONE: 'findOne_class_membership',
  FIND_BY_CLASS: 'findByClass_class_memberships',
  FIND_BY_USER: 'findByUser_class_memberships',
  FIND_ACTIVE: 'findActive_class_memberships',
  FIND_DELEGATES: 'findDelegates_class_memberships',
  FIND_HISTORY: 'findHistory_class_memberships',
  ADD_MEMBERS_BULK: 'addMembers_bulk_class_memberships',
  UPDATE: 'update_class_membership',
  UPDATE_DELEGATES: 'updateDelegates_class_memberships',
  DELETE: 'delete_class_membership',
  STATS: 'stats_class_memberships',
} as const

/**
 * Messages de log pour les opérations
 */
export const CLASS_MEMBERSHIP_LOG_MESSAGES = {
  CREATING: (userId: string, classId: string) =>
    `[User ${userId}] Création membership classe: ${classId}`,
  CREATED: (id: string) => `Class membership created successfully: ${id}`,
  UPDATING: (userId: string, id: string) =>
    `[User ${userId}] Mise à jour membership classe: ${id}`,
  UPDATED: (id: string) => `Class membership updated successfully: ${id}`,
  DELETING: (userId: string, id: string) =>
    `[User ${userId}] Suppression membership classe: ${id}`,
  DELETED: (id: string) => `Class membership deleted successfully: ${id}`,
  FOUND: (count: number, userId: string) =>
    `Found ${count} class memberships for user ${userId}`,
  BULK_ADD_START: (classId: string, count: number) =>
    `Adding ${count} members to class ${classId}`,
  BULK_ADD_COMPLETE: (added: number, skipped: number, errors: number) =>
    `Bulk add complete: ${added} added, ${skipped} skipped, ${errors} errors`,
  DELEGATES_UPDATED: (classId: string, count: number) =>
    `Délégués mis à jour pour classe ${classId}: ${count} délégués`,
} as const

/**
 * Contextes d'erreur pour ServiceErrorHandler
 */
export const CLASS_MEMBERSHIP_ERROR_CONTEXTS = {
  CREATE: 'create class membership',
  FETCH_ALL: 'fetch class memberships',
  FETCH_ONE: 'fetch class membership',
  FETCH_BY_CLASS: 'fetch memberships by class',
  FETCH_BY_USER: 'fetch memberships by user',
  FETCH_ACTIVE: 'fetch active memberships',
  FETCH_DELEGATES: 'fetch class delegates',
  FETCH_HISTORY: 'fetch class history',
  ADD_MEMBERS_BULK: 'add members bulk',
  UPDATE: 'update class membership',
  UPDATE_DELEGATES: 'update class delegates',
  DELETE: 'delete class membership',
  STATS: 'fetch class member stats',
} as const

/**
 * Actions PolicyEngine
 */
export const CLASS_MEMBERSHIP_POLICY_ACTIONS = {
  CREATE: 'classMembership:create',
  VIEW: 'classMembership:view',
  UPDATE: 'classMembership:update',
  DELETE: 'classMembership:delete',
  MANAGE_DELEGATES: 'classMembership:manageDelegates',
  VIEW_STATS: 'classMembership:viewStats',
} as const
